import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import type { EvoPermissionCheckSnapshot } from '../../services/firebaseFunctions';
import EvoPermissionCheckDialog from './EvoPermissionCheckDialog';
import { countSteps, summarizeCounts } from './evoPermissionCheckStatus';

export interface EvoCredentialsValues {
  dns: string;
  token: string;
  nativeIntegration: boolean;
}

export interface EvoCredentialsSubmitResult {
  success: boolean;
  error?: string;
  conflictingWebhookConfigId?: string;
}

/** Prova do teste de permissões que autoriza a gravação (REEDY-141). */
export interface EvoCredentialsProof {
  runId: string;
  /** Resumo do teste, por exemplo "7 de 8 OK e 1 sem venda para testar". */
  summary: string;
}

interface Props {
  open: boolean;
  /** Credenciais salvas (edição) ou `null` (cadastro). */
  initial: EvoCredentialsValues | null;
  /** ID do cadastro aberto: o "Abrir cadastro" do conflito só aparece para outro cadastro. */
  currentConfigId: string;
  onClose: () => void;
  /** Grava as credenciais com a prova do teste. O pai fecha o diálogo em caso de sucesso. */
  onSubmit: (values: EvoCredentialsValues, proof: EvoCredentialsProof) => Promise<EvoCredentialsSubmitResult>;
  onOpenConflict: (configId: string) => void;
  /** O teste pode ter registrado um aviso de webhook de teste não apagado: recarrega o cadastro. */
  onReloadConfig?: () => void;
}

interface SubmitError {
  message: string;
  conflictingWebhookConfigId?: string;
  /** Valores enviados quando o erro voltou (o conflito só some se DNS ou token mudarem). */
  dns: string;
  token: string;
}

const NATIVE_HELP_OFF = 'Marque se este token foi gerado pela automação da EVO.';
const NATIVE_HELP_ON = 'As chamadas à EVO com este token levarão o cabeçalho da integração nativa.';
const NATIVE_HELP_RULE = 'Você pode corrigir esta opção sem trocar o token. Se a EVO confirmar webhooks Reedy ativos, remova-os antes. Se a consulta falhar ou retornar zero webhooks Reedy, a edição continua disponível.';

const TEST_BEFORE_SAVE_NOTE = 'Ao salvar, o Reedy testa as permissões do token na EVO (leva alguns segundos). As credenciais só são gravadas se o teste passar.';

/**
 * Diálogo de credenciais da conta EVO: DNS, token e tipo do token (REEDY-114, D1 e D5).
 * Salvar passa pelo teste de permissões (REEDY-141, J2): o teste usa os valores do formulário e só então grava.
 */
const EvoWebhookCredentialsDialog: React.FC<Props> = ({ open, initial, currentConfigId, onClose, onSubmit, onOpenConflict, onReloadConfig }) => {
  const [dns, setDns] = useState('');
  const [token, setToken] = useState('');
  const [nativeIntegration, setNativeIntegration] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [phase, setPhase] = useState<'form' | 'testing'>('form');
  // O token só vive aqui (estado do diálogo) enquanto o teste corre; nunca em storage.
  const [testValues, setTestValues] = useState<EvoCredentialsValues | null>(null);
  const [error, setError] = useState<SubmitError | null>(null);

  useEffect(() => {
    if (!open) return;
    setDns(initial?.dns ?? '');
    setToken(initial?.token ?? '');
    setNativeIntegration(initial?.nativeIntegration ?? false);
    setShowToken(false);
    setPhase('form');
    setTestValues(null);
    setError(null);
    // Só reinicia ao abrir: o pai atualiza `initial` depois de salvar, com o diálogo fechando.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isEdit = initial !== null;
  const tokenChanged = isEdit && token.trim() !== initial.token.trim();
  const isConflict = Boolean(error?.conflictingWebhookConfigId);
  const conflictBlocked = isConflict && dns.trim() === error?.dns && token.trim() === error?.token;
  const saveDisabled = !dns.trim() || !token.trim() || conflictBlocked;

  const handleSave = () => {
    setError(null);
    setTestValues({ dns: dns.trim(), token: token.trim(), nativeIntegration });
    setPhase('testing');
  };

  const backToForm = (nextError: SubmitError | null) => {
    setError(nextError);
    setPhase('form');
  };

  const handleSaveCredentials = async (runId: string, snapshot: EvoPermissionCheckSnapshot) => {
    const values = testValues as EvoCredentialsValues;
    const result = await onSubmit(values, { runId, summary: summarizeCounts(countSteps(snapshot.steps)) });
    if (!result.success && result.conflictingWebhookConfigId) {
      // Corrida rara (o conflito já é checado ao iniciar o teste): volta ao formulário como o conflito de hoje.
      backToForm({
        message: result.error || 'Erro ao salvar credenciais',
        conflictingWebhookConfigId: result.conflictingWebhookConfigId,
        dns: values.dns,
        token: values.token,
      });
    }
    return result;
  };

  return (
    <>
      <Dialog open={open && phase === 'form'} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="evo-credentials-title">
        <DialogTitle id="evo-credentials-title">{isEdit ? 'Editar credenciais' : 'Cadastrar credenciais'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert
                severity="error"
                action={
                  error.conflictingWebhookConfigId && error.conflictingWebhookConfigId !== currentConfigId ? (
                    <Button color="inherit" size="small" onClick={() => onOpenConflict(error.conflictingWebhookConfigId as string)}>
                      Abrir cadastro
                    </Button>
                  ) : undefined
                }
              >
                {error.message}
              </Alert>
            )}
            <TextField fullWidth required label="DNS" value={dns} onChange={(e) => setDns(e.target.value)} />
            <TextField
              fullWidth
              required
              label="Token"
              type={showToken ? 'text' : 'password'}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label={showToken ? 'Ocultar token' : 'Mostrar token'}
                      onClick={() => setShowToken((visible) => !visible)}
                    >
                      {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {tokenChanged && (
              <Alert severity="warning" role="status">
                Você alterou o token. Confirme se o novo token é de integração nativa.
              </Alert>
            )}
            <FormControl>
              <FormControlLabel
                control={<Checkbox checked={nativeIntegration} onChange={(e) => setNativeIntegration(e.target.checked)} />}
                label="Token de integração nativa"
              />
              <FormHelperText sx={{ mt: 0, ml: 4 }}>{nativeIntegration ? NATIVE_HELP_ON : NATIVE_HELP_OFF} {NATIVE_HELP_RULE}</FormHelperText>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {TEST_BEFORE_SAVE_NOTE}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          {conflictBlocked && (
            <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 200px', pl: 1 }}>
              Altere o DNS ou o token para poder salvar.
            </Typography>
          )}
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saveDisabled}>
            Salvar e testar
          </Button>
        </DialogActions>
      </Dialog>
      <EvoPermissionCheckDialog
        open={open && phase === 'testing'}
        configId={currentConfigId}
        mode="credentials"
        credentials={testValues ?? undefined}
        onSaveCredentials={handleSaveCredentials}
        onStartError={({ message, conflictingWebhookConfigId }) =>
          backToForm({ message, conflictingWebhookConfigId, dns: testValues?.dns ?? '', token: testValues?.token ?? '' })
        }
        onDone={({ maybeLeftover }) => {
          if (maybeLeftover) onReloadConfig?.();
          backToForm(null);
        }}
      />
    </>
  );
};

export default EvoWebhookCredentialsDialog;
